import React, { useState } from "react";
import clone from "lodash.clonedeep";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
    Checkbox, FormGroup, FormControl, Row, Col, ControlLabel, MenuItem, Dropdown,
    Button, ButtonGroup, Glyphicon, ListGroupItem, Modal
} from "react-bootstrap";
import Select from "react-select";
import MoveIcon from "../../../Widgets/components/Icons/MoveIcon";
import { usePagesContext } from "../../context/PagesContext";

const buttonOptions = [
    {
        type: 'action',
        title: 'Кнопка-действие'
    },
    {
        type: 'navigationInner',
        title: 'Кнопка-ссылка на раздел платформы'
    },
    {
        type: 'navigationOuter',
        title: 'Кнопка-ссылка на сторонний ресурс'
    },
];

const buttonActionTypes = [{
    type: 'askQuestion',
    label: 'Задать вопрос'
}];

const DefaultButtonTemplate = ({ button, onChange, onSave }) => {
    return <>
        <FormGroup>
            <Row>
                <Col xs={6}>
                    <ControlLabel>Текст [Ru]</ControlLabel>
                    <FormControl
                        type="text"
                        value={button.textRu}
                        onChange={(e) => onChange('textRu', e.target.value)}
                        onBlur={() => onSave({ textRu: button.textRu })}
                    />
                </Col>
                <Col xs={6}>
                    <ControlLabel>Текст [En]</ControlLabel>
                    <FormControl
                        type="text"
                        value={button.textEn}
                        onChange={(e) => onChange('textEn', e.target.value)}
                        onBlur={() => onSave({ textEn: button.textEn })}
                    />
                </Col>
            </Row>
        </FormGroup>
        <FormGroup>
            <Checkbox
                checked={button.accentColor}
                onChange={(e) => onSave({ accentColor: e.target.checked })}
            >
                Использовать акцентный цвет
            </Checkbox>
        </FormGroup>
    </>
}

const ActionButtonTemplate = ({ button, onSave }) => {
    const options = buttonActionTypes.map(({ type, label }) => ({ value: type, label }));

    return <FormGroup>
        <Row>
            <Col xs={12}>
                <Select
                    value={options.find(({ value }) => button.actionType === value)}
                    onChange={({ value }) => onSave({ actionType: value })}
                    options={options}
                />
            </Col>
        </Row>
    </FormGroup>
};

const NavigationInnerButtonTemplate = ({ button, onSave }) => {
    const { pages } = usePagesContext();
    const options = (pages || []).filter(page => page.path && page.path !== 'lobby').map((page) => ({ value: page.path, label: page.titleRu }));

    const handlePageChange = (value) => {
        const page = pages?.find((page) => page.path === value) || {};
        onSave({ navigateTo: value, textRu: page.titleRu, textEn: page.titleEn });
    }

    return <FormGroup>
        <Row>
            <Col xs={12}>
                <Select
                    value={options.find(({ value }) => button.navigateTo === value)}
                    onChange={({ value }) => handlePageChange(value)}
                    options={options}
                />
            </Col>
        </Row>
    </FormGroup>
};

const NavigationOuterButtonTemplate = ({ button, onChange, onSave }) => {

    return <FormGroup>
        <Row>
            <Col xs={12}>
                <ControlLabel>Ссылка</ControlLabel>
                <FormControl
                    type="text"
                    value={button.navigateTo}
                    onChange={(e) => onChange('navigateTo', e.target.value)}
                    onBlur={() => onSave({ navigateTo: button.navigateTo })}
                />
            </Col>
        </Row>
    </FormGroup>
};

const ButtonEditModal = ({ button, handleSaveButton, handleChangeButton }) => {
    if (!button) return null;

    const editorTemplates = {
        action: (button) => <ActionButtonTemplate button={button} onSave={handleSaveButton} onChange={handleChangeButton} />,
        navigationInner: (button) => <NavigationInnerButtonTemplate button={button} onSave={handleSaveButton} onChange={handleChangeButton} />,
        navigationOuter: (button) => <NavigationOuterButtonTemplate button={button} onSave={handleSaveButton} onChange={handleChangeButton} />,
    }

    return <>
        <Modal.Header closeButton>
            <Modal.Title>{buttonOptions.find(({ type }) => type === button.type)?.title || ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {editorTemplates[button.type](button)}
            <DefaultButtonTemplate button={button} onSave={handleSaveButton} onChange={handleChangeButton} />
        </Modal.Body>
    </>
}

export default ({ blockProperties, onSave, onUpdateBlockProperties }) => {

    const buttons = blockProperties?.buttons || [];

    const [editingButton, setEditingButton] = useState(null);

    const handleAddButton = (type) => {
        const newButton = {
            id: buttons.reduce((id, cur) => Number(cur.id) > id ? Number(cur.id) : id, 0) + 1,
            order: buttons.reduce((order, cur) => cur.order > order ? cur.order : order, 0) + 1,
            type,
            actionType: type === 'action' ? buttonActionTypes[0].type : '',
            navigateTo: type === 'navigationInner' ? 'broadcast' : '',
            textRu: type === 'action' ? 'Задать вопрос' : type === 'navigationInner' ? 'Трансляция' : '',
            textEn: type === 'action' ? 'Ask a question' : type === 'navigationInner' ? 'Broadcast' : '',
            accentColor: false,
        }
        onSave({ buttons: [...buttons, newButton] });
    }

    const handleDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const _buttons = clone(buttons);
        const [removed] = _buttons.splice(result.source.index, 1);
        _buttons.splice(result.destination.index, 0, removed);
        const _result = { buttons: _buttons.map((button, idx) => ({ ...button, order: idx + 1 })) };
        onUpdateBlockProperties(_result)
        onSave(_result);
    }

    const handleDeleteButton = (buttonID) => {
        const _buttons = buttons.filter(({ id }) => id !== buttonID);
        const _result = { buttons: _buttons.map((button, idx) => ({ ...button, order: idx + 1 })) };
        onUpdateBlockProperties(_result);
        onSave(_result);
    }

    const handleSaveButton = (properties) => {
        if (!editingButton) return;
        const _editingButton = { ...editingButton, ...properties };
        setEditingButton(_editingButton)
        const _result = { buttons: buttons.map((button) => button.id === editingButton.id ? _editingButton : button) };
        onUpdateBlockProperties(_result);
        onSave(_result);

    }

    const handleChangeButton = (key, value) => {
        setEditingButton(button => ({ ...button, [key]: value }))
    }



    return (
        <div>
            <Row>
                <Col xs={12}>
                    <Dropdown
                        id="add-button"
                    >
                        <Dropdown.Toggle>
                            Добавить кнопку
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {
                                buttonOptions.map((button) => (
                                    <MenuItem
                                        key={button.type}
                                        onClick={() => handleAddButton(button.type)}
                                    >
                                        {button.title}
                                    </MenuItem>
                                ))
                            }
                        </Dropdown.Menu>
                    </Dropdown>
                </Col>
            </Row>
            <Row style={{ marginTop: '20px' }}>
                <Col xs={12}>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="droppable">
                            {(provided) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {buttons.map((button, index) => (
                                        <Draggable
                                            key={button.id}
                                            draggableId={`${button.id || index}`}
                                            index={index}
                                        >
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        left: 0,
                                                        top: provided.draggableProps.style?.top - 22 || 0
                                                    }}
                                                >
                                                    <ListGroupItem
                                                        key={button.id}
                                                        style={{
                                                            padding: '4px 4px 4px 12px',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            height: '44px',
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', gap: '0.5rem' }}>
                                                            <MoveIcon />
                                                            <span>{button.textRu || button.textEn}</span>
                                                            <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', opacity: 0.85 }}>{buttonOptions.find(({ type }) => type === button.type)?.title || ''}</span>
                                                        </div>
                                                        <ButtonGroup>
                                                            <Button onClick={() => setEditingButton(button)}>
                                                                <Glyphicon glyph="edit" />
                                                            </Button>
                                                            <Button bsStyle="danger"><Glyphicon glyph="trash" onClick={() => handleDeleteButton(button.id)} /></Button>
                                                        </ButtonGroup>
                                                    </ListGroupItem>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Col>
            </Row>
            <Modal
                show={!!editingButton}
                onHide={() => setEditingButton(null)}
            >
                <ButtonEditModal button={editingButton} handleSaveButton={handleSaveButton} handleChangeButton={handleChangeButton} />
            </Modal>
        </div>
    )
};
